import React, { useState, useEffect } from 'react';
import { X, Loader2, Save, AlertCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import { FeatureName, FEATURE_DESCRIPTIONS } from '../../types/featureFlags';

interface FeatureFlag {
  id: number;
  companyId: number;
  featureName: string;
  isEnabled: boolean;
  enabledDate?: string | null;
  enabledBy?: string | null;
  enabledByName?: string | null;
  notes?: string | null;
}

interface CompanyFeatureFlagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

const CompanyFeatureFlagsModal: React.FC<CompanyFeatureFlagsModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName
}) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && companyId) {
      loadFeatureFlags();
    }
  }, [isOpen, companyId]);

  const loadFeatureFlags = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCompanyFeatureFlags(companyId);
      if (response.success && response.data) {
        setFeatureFlags(response.data);
        // Initialize notes state
        const notesMap: Record<string, string> = {};
        response.data.forEach(flag => {
          if (flag.notes) {
            notesMap[flag.featureName] = flag.notes;
          }
        });
        setNotes(notesMap);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeature = async (featureName: string, currentEnabled: boolean) => {
    try {
      setSaving(featureName);
      const newEnabled = !currentEnabled;
      const note = notes[featureName] || '';

      await apiService.toggleFeatureFlag(companyId, featureName, newEnabled, note);

      // Update local state
      setFeatureFlags(prev => prev.map(flag => 
        flag.featureName === featureName
          ? { ...flag, isEnabled: newEnabled, enabledDate: newEnabled ? new Date().toISOString() : null }
          : flag
      ));

      toast.success(`Feature "${featureName}" ${newEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update feature flag');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveNotes = async (featureName: string) => {
    try {
      setSaving(featureName);
      const flag = featureFlags.find(f => f.featureName === featureName);
      if (!flag) return;

      await apiService.toggleFeatureFlag(companyId, featureName, flag.isEnabled, notes[featureName] || '');

      toast.success('Notes saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save notes');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Feature Flags - ${companyName}`} maxWidth="3xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Feature Flags - ${companyName}`} maxWidth="3xl">
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Feature Flag Management</p>
              <p>
                Enable or disable features for this company. Feature flags override plan-based restrictions.
                Changes are logged in the audit trail.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {featureFlags.map((flag) => (
            <div
              key={flag.featureName}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={flag.isEnabled}
                        onChange={() => handleToggleFeature(flag.featureName, flag.isEnabled)}
                        disabled={saving === flag.featureName}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {flag.featureName}
                      </span>
                    </label>
                    {saving === flag.featureName && (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 ml-8 mb-3">
                    {FEATURE_DESCRIPTIONS[flag.featureName as FeatureName] || 'No description available'}
                  </p>
                  
                  {flag.isEnabled && flag.enabledDate && (
                    <div className="ml-8 text-xs text-gray-500 dark:text-gray-500 mb-2">
                      Enabled on {new Date(flag.enabledDate).toLocaleDateString()}
                      {flag.enabledByName && ` by ${flag.enabledByName}`}
                    </div>
                  )}

                  <div className="ml-8 space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes (optional)
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        value={notes[flag.featureName] || ''}
                        onChange={(e) => setNotes(prev => ({ ...prev, [flag.featureName]: e.target.value }))}
                        placeholder="Add notes about this feature flag..."
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        rows={2}
                      />
                      <button
                        onClick={() => handleSaveNotes(flag.featureName)}
                        disabled={saving === flag.featureName}
                        className="btn-primary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default CompanyFeatureFlagsModal;

